import { getContrastColor } from '@/lib/colorUtils';
import { useState, useEffect } from "react";
import { Lightbulb, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { logger } from '@/lib/logger';
import { RichTextEditor } from "@/components/RichTextEditor";

interface AgentInsightsManagerProps {
  northId: string;
  isPublished?: boolean;
  onUpdate: () => void;
  secondaryColor?: string | null;
}

export const AgentInsightsManager = ({ northId, isPublished = false, onUpdate, secondaryColor }: AgentInsightsManagerProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [generating, setGenerating] = useState(false);
  const [showOnReport, setShowOnReport] = useState(false);
  const [interestLevel, setInterestLevel] = useState<string>("");
  const [buyerSentiment, setBuyerSentiment] = useState<string>("");
  const [priceCompetitiveness, setPriceCompetitiveness] = useState<string>("");
  const [conditionFeedback, setConditionFeedback] = useState<string>("");
  const [marketingPerformance, setMarketingPerformance] = useState<string>("");
  const [expectedActivity, setExpectedActivity] = useState<string>("");
  const [sellerFlexibility, setSellerFlexibility] = useState<string>("");
  const [urgencyLevel, setUrgencyLevel] = useState<string>("");
  const [customNotes, setCustomNotes] = useState<string>("");
  const [agentInsightsSummary, setAgentInsightsSummary] = useState<string>("");

  // Fetch existing agent insights
  const { data: existingInsights } = useQuery({
    queryKey: ['agent-insights', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('agent_insights')
        .select('*')
        .eq('north_id', northId)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') throw error;
      return data;
    },
  });

  // Fetch comprehensive property data
  const { data: propertyData } = useQuery({
    queryKey: ['north-comprehensive', northId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('norths')
        .select('address, city, state, price, bedrooms, bathrooms, square_feet, days_on_zillow, status, views_count, saves_count, showings_count, price_changes_count, executive_summary, showings_summary, comps_summary, show_agent_insights_on_report')
        .eq('id', northId)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Load show on report toggle setting
  useEffect(() => {
    if (propertyData?.show_agent_insights_on_report !== null && propertyData?.show_agent_insights_on_report !== undefined) {
      setShowOnReport(propertyData.show_agent_insights_on_report);
    }
  }, [propertyData]);

  // Load existing agent insights into form
  useEffect(() => {
    if (existingInsights) {
      setInterestLevel(existingInsights.interest_level || "");
      setBuyerSentiment(existingInsights.buyer_sentiment || "");
      setPriceCompetitiveness(existingInsights.price_competitiveness || "");
      setConditionFeedback(existingInsights.condition_feedback || "");
      setMarketingPerformance(existingInsights.marketing_performance || "");
      setExpectedActivity(existingInsights.expected_activity || "");
      setSellerFlexibility(existingInsights.seller_flexibility || "");
      setUrgencyLevel(existingInsights.urgency_level || "");
      setCustomNotes(existingInsights.custom_notes || "");
      setAgentInsightsSummary(existingInsights.ai_summary || "");
    }
  }, [existingInsights]);

  const generateAgentInsights = async () => {
    if (!interestLevel && !buyerSentiment && !priceCompetitiveness) {
      toast({
        title: "Error",
        description: "Please answer at least a few questions before generating insights",
        variant: "destructive",
      });
      return;
    }

    setGenerating(true);

    try {
      const agentInsights = {
        interest_level: interestLevel,
        buyer_sentiment: buyerSentiment,
        price_competitiveness: priceCompetitiveness,
        condition_feedback: conditionFeedback,
        marketing_performance: marketingPerformance,
        expected_activity: expectedActivity,
        seller_flexibility: sellerFlexibility,
        urgency_level: urgencyLevel,
        custom_notes: customNotes,
      };

      const { data, error } = await supabase.functions.invoke('generate-agent-insights', {
        body: { agentInsights, propertyData }
      });

      if (error) {
        logger.error('Edge function error:', error);
        throw error;
      }

      if (data.error) {
        logger.error('Edge function returned error:', data.error);
        if (data.error.includes('Rate limit')) {
          toast({
            title: "Error",
            description: "Rate limit exceeded. Please wait a moment and try again.",
            variant: "destructive",
          });
        } else if (data.error.includes('credits exhausted')) {
          toast({
            title: "Error",
            description: "AI credits exhausted. Please add more credits to continue.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: data.error,
            variant: "destructive",
          });
        }
        return;
      }

      // Save insights to database
      const { error: saveError } = await supabase
        .from('agent_insights')
        .upsert({
          north_id: northId,
          ...agentInsights,
          ai_summary: data.aiSummary,
        }, { onConflict: 'north_id' });

      if (saveError) throw saveError;

      setAgentInsightsSummary(data.aiSummary);

      toast({
        title: "Success",
        description: "Agent insights generated successfully!",
      });

      onUpdate();
    } catch (error: any) {
      logger.error('Error generating insights:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate insights",
        variant: "destructive",
      });
    } finally {
      setGenerating(false);
    }
  };

  const saveAgentInsights = async () => {
    try {
      const agentInsights = {
        north_id: northId,
        interest_level: interestLevel,
        buyer_sentiment: buyerSentiment,
        price_competitiveness: priceCompetitiveness,
        condition_feedback: conditionFeedback,
        marketing_performance: marketingPerformance,
        expected_activity: expectedActivity,
        seller_flexibility: sellerFlexibility,
        urgency_level: urgencyLevel,
        custom_notes: customNotes,
        ai_summary: agentInsightsSummary || null,
      };

      const { error } = await supabase
        .from('agent_insights')
        .upsert(agentInsights, { onConflict: 'north_id' });

      if (error) throw error;

      // Invalidate queries to sync with other components
      queryClient.invalidateQueries({ queryKey: ['agent-insights', northId] });
      queryClient.invalidateQueries({ queryKey: ['agent-insights-summary', northId] });
      queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });

      toast({
        title: "Success",
        description: "Agent insights saved successfully!",
      });

      onUpdate();
    } catch (error: any) {
      logger.error('Error saving insights:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save insights",
        variant: "destructive",
      });
    }
  };

  const handleToggleVisibility = async (checked: boolean) => {
    try {
      const { error } = await supabase
        .from('norths')
        .update({ show_agent_insights_on_report: checked })
        .eq('id', northId);

      if (error) throw error;

      setShowOnReport(checked);
      
      // Small delay to ensure database write is fully propagated
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Notify parent component to refetch north data (for preview update)
      if (onUpdate) {
        await onUpdate();
      }
      
      toast({
        title: "Success",
        description: checked
          ? "Agent Insights will show on report"
          : "Agent Insights hidden from report",
      });
    } catch (error) {
      logger.error('Error updating visibility:', error);
      toast({
        title: "Error",
        description: "Failed to update visibility",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6 md:space-y-8 p-4 md:p-6">
      {isPublished && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            This report is published. Click "Unpublish" to make changes.
          </p>
        </div>
      )}

      <div className="space-y-3">
        <div>
          <div className="mb-2">
            <h2 className="text-xl md:text-2xl font-bold text-foreground">Agent Insights & Recommendations</h2>
          </div>
          <p className="text-sm md:text-base text-muted-foreground">
            Answer questions about this listing to get AI-powered strategic insights and recommendations.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <Switch
              id="show-agent-insights-on-report"
              checked={showOnReport}
              onCheckedChange={handleToggleVisibility}
              disabled={isPublished}
              style={showOnReport ? { backgroundColor: '#22c55e' } : undefined}
            />
            <Label htmlFor="show-agent-insights-on-report" className="text-sm font-medium cursor-pointer whitespace-nowrap">
              Show on Report
            </Label>
          </div>
        </div>
      </div>

      {/* Market & Activity Assessment */}
      <div className="space-y-6">
        <h3 className="font-semibold text-lg">Market & Activity Assessment</h3>
        
        <div className="space-y-2">
          <Label>1. How would you describe interest so far?</Label>
          <RadioGroup value={interestLevel} onValueChange={setInterestLevel} disabled={isPublished}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Strong" id="interest-strong" />
              <Label htmlFor="interest-strong" className="font-normal cursor-pointer">Strong</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Steady" id="interest-steady" />
              <Label htmlFor="interest-steady" className="font-normal cursor-pointer">Steady</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Slow" id="interest-slow" />
              <Label htmlFor="interest-slow" className="font-normal cursor-pointer">Slow</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Minimal" id="interest-minimal" />
              <Label htmlFor="interest-minimal" className="font-normal cursor-pointer">Minimal</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label>2. Buyer sentiment from showings?</Label>
          <RadioGroup value={buyerSentiment} onValueChange={setBuyerSentiment} disabled={isPublished}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Positive" id="sentiment-positive" />
              <Label htmlFor="sentiment-positive" className="font-normal cursor-pointer">Positive</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Mixed" id="sentiment-mixed" />
              <Label htmlFor="sentiment-mixed" className="font-normal cursor-pointer">Mixed</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Negative" id="sentiment-negative" />
              <Label htmlFor="sentiment-negative" className="font-normal cursor-pointer">Negative</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Not enough data" id="sentiment-nodata" />
              <Label htmlFor="sentiment-nodata" className="font-normal cursor-pointer">Not enough data</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label>3. How competitively is the home priced?</Label>
          <RadioGroup value={priceCompetitiveness} onValueChange={setPriceCompetitiveness} disabled={isPublished}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Very competitive" id="price-verycomp" />
              <Label htmlFor="price-verycomp" className="font-normal cursor-pointer">Very competitive</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Competitive" id="price-comp" />
              <Label htmlFor="price-comp" className="font-normal cursor-pointer">Competitive</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Slightly high" id="price-high" />
              <Label htmlFor="price-high" className="font-normal cursor-pointer">Slightly high</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="High" id="price-veryhigh" />
              <Label htmlFor="price-veryhigh" className="font-normal cursor-pointer">High</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Not sure" id="price-notsure" />
              <Label htmlFor="price-notsure" className="font-normal cursor-pointer">Not sure</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="condition-feedback">4. Condition or feedback themes you're seeing?</Label>
          <Select value={conditionFeedback} onValueChange={setConditionFeedback} disabled={isPublished}>
            <SelectTrigger id="condition-feedback">
              <SelectValue placeholder="Select feedback theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Condition concerns">Condition concerns</SelectItem>
              <SelectItem value="Size/space concerns">Size/space concerns</SelectItem>
              <SelectItem value="Layout concerns">Layout concerns</SelectItem>
              <SelectItem value="Location concerns">Location concerns</SelectItem>
              <SelectItem value="None/feedback is scattered">None/feedback is scattered</SelectItem>
              <SelectItem value="Not enough feedback">Not enough feedback</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Agent Strategy Evaluation */}
      <div className="space-y-6">
        <h3 className="font-semibold text-lg">Agent Strategy Evaluation</h3>

        <div className="space-y-2">
          <Label>5. How strong is marketing performance?</Label>
          <RadioGroup value={marketingPerformance} onValueChange={setMarketingPerformance} disabled={isPublished}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Strong (high engagement)" id="marketing-strong" />
              <Label htmlFor="marketing-strong" className="font-normal cursor-pointer">Strong (high engagement)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Moderate" id="marketing-moderate" />
              <Label htmlFor="marketing-moderate" className="font-normal cursor-pointer">Moderate</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Low" id="marketing-low" />
              <Label htmlFor="marketing-low" className="font-normal cursor-pointer">Low</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Very low" id="marketing-verylow" />
              <Label htmlFor="marketing-verylow" className="font-normal cursor-pointer">Very low</Label>
            </div>
          </RadioGroup>
        </div>

        <div className="space-y-2">
          <Label htmlFor="expected-activity">6. What pace of activity do you expect in the next 2 weeks?</Label>
          <Select value={expectedActivity} onValueChange={setExpectedActivity} disabled={isPublished}>
            <SelectTrigger id="expected-activity">
              <SelectValue placeholder="Select expected activity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Increase">Increase</SelectItem>
              <SelectItem value="Stay the same">Stay the same</SelectItem>
              <SelectItem value="Decrease">Decrease</SelectItem>
              <SelectItem value="Not sure">Not sure</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="seller-flexibility">7. Seller flexibility?</Label>
          <Select value={sellerFlexibility} onValueChange={setSellerFlexibility} disabled={isPublished}>
            <SelectTrigger id="seller-flexibility">
              <SelectValue placeholder="Select seller flexibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Very flexible">Very flexible</SelectItem>
              <SelectItem value="Somewhat flexible">Somewhat flexible</SelectItem>
              <SelectItem value="Not flexible">Not flexible</SelectItem>
              <SelectItem value="Unknown">Unknown</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label>8. Urgency level on this listing</Label>
          <RadioGroup value={urgencyLevel} onValueChange={setUrgencyLevel} disabled={isPublished}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="High urgency" id="urgency-high" />
              <Label htmlFor="urgency-high" className="font-normal cursor-pointer">High urgency</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Medium urgency" id="urgency-medium" />
              <Label htmlFor="urgency-medium" className="font-normal cursor-pointer">Medium urgency</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="Low urgency" id="urgency-low" />
              <Label htmlFor="urgency-low" className="font-normal cursor-pointer">Low urgency</Label>
            </div>
          </RadioGroup>
        </div>
      </div>

      {/* Custom Notes */}
      <div className="space-y-4">
        <h3 className="font-semibold text-lg">Custom Notes</h3>
        <div className="space-y-2">
          <Label htmlFor="custom-notes">Anything you want the AI to consider? (Optional)</Label>
          <Textarea
            id="custom-notes"
            value={customNotes}
            onChange={(e) => setCustomNotes(e.target.value)}
            placeholder="Add any additional context, concerns, or observations..."
            rows={4}
            disabled={isPublished}
          />
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button
          onClick={generateAgentInsights}
          disabled={generating || isPublished}
          className="flex-1"
          style={{ backgroundColor: secondaryColor || '#1b489b', color: getContrastColor(secondaryColor || '#1b489b') }}
        >
          {generating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Generating Insights...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Generate AI Insights
            </>
          )}
        </Button>
        <Button
          onClick={saveAgentInsights}
          variant="outline"
          disabled={generating || isPublished}
          style={{ borderColor: secondaryColor || '#1b489b', color: secondaryColor || '#1b489b' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = secondaryColor || '#1b489b';
            e.currentTarget.style.color = 'white';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = secondaryColor || '#1b489b';
          }}
        >
          Save Answers
        </Button>
      </div>

      {/* Display AI-Generated Insights */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          <h4 className="font-semibold">AI-Generated Insights Summary</h4>
        </div>
        <RichTextEditor
          value={agentInsightsSummary}
          onChange={setAgentInsightsSummary}
          onSave={async (summary) => {
            // Save agent insights summary
            const { error } = await supabase
              .from('agent_insights')
              .upsert({
                north_id: northId,
                ai_summary: summary || null,
              }, { onConflict: 'north_id' });
            
            if (!error) {
              queryClient.invalidateQueries({ queryKey: ['agent-insights', northId] });
              queryClient.invalidateQueries({ queryKey: ['agent-insights-summary', northId] });
              queryClient.invalidateQueries({ queryKey: ['ai-summaries', northId] });
            }
          }}
          placeholder="Answer the questions above and click 'Generate AI Insights' to create strategic recommendations..."
          disabled={isPublished}
          className="min-h-48"
        />
        {agentInsightsSummary && (
          <p className="text-xs text-muted-foreground mt-2">
            This summary also appears in the AI Summaries menu on the report.
          </p>
        )}
      </div>
    </div>
  );
};
